/**
 * Install setup page — Wave 1a / W1a-19.
 *
 * Public route at `/setup/<one-time-token>`. The install script
 * generates the token at deployment time and prints the raw value to
 * stdout; the operator opens this URL once, fills the form, and the
 * token self-destructs.
 *
 * Two-phase flow:
 *   Phase 1 — token validation on mount (GET /api/v1/setup/install-token/:token).
 *             404 / 410 → friendly error page with the discriminated code.
 *   Phase 2 — single submit form (sysadmin enrollment + first tenant create
 *             reused via the ProvisionTenantWizard).
 *
 * On success: display TOTP QR (if enrollment was requested) + the
 * invitation URL once + redirect to /login.
 *
 * NO auth context — this route runs before any user/tenant exists.
 * The token IS the auth.
 */

import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Loader2,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Copy,
  Check,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  useValidateInstallToken,
  useRedeemInstall,
  type RedeemInstallBody,
  type RedeemInstallResponse,
} from '@/lib/use-tenant-provisioning';
import { ProvisionTenantWizard } from '@/components/platform/provision-tenant-wizard';

export const Route = createFileRoute('/setup/$token')({
  component: SetupTokenPage,
});

function SetupTokenPage() {
  const { token } = Route.useParams();
  const validation = useValidateInstallToken(token);
  const redeem = useRedeemInstall();
  const [phase, setPhase] = useState<'sysadmin' | 'tenant' | 'done'>('sysadmin');
  const [sysadmin, setSysadmin] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    firstName: '',
    lastName: '',
    totpEnrollment: true,
  });
  const [sysadminErrors, setSysadminErrors] = useState<Record<string, string>>({});
  const [installResult, setInstallResult] = useState<RedeemInstallResponse | null>(null);
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const navigate = useNavigate();

  // ── Validation outcome rendering ───────────────────────────────────────────
  if (validation.isLoading) {
    return (
      <CenteredCard>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-3 text-sm text-muted-foreground">Validating install token…</span>
        </div>
      </CenteredCard>
    );
  }

  if (validation.isError || !validation.data) {
    const code = (validation.error as { code?: string } | null)?.code ?? 'INSTALL_TOKEN_INVALID';
    const message = validation.error?.message ?? 'Install token is not valid.';
    return (
      <CenteredCard>
        <div className="text-center py-8 space-y-3">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
          <div className="text-lg font-medium">Setup link unavailable</div>
          <div className="text-sm text-muted-foreground">{message}</div>
          <div className="text-xs font-mono text-muted-foreground">code: {code}</div>
          <p className="text-xs text-muted-foreground pt-3">
            Ask your operator to regenerate the install token.
          </p>
        </div>
      </CenteredCard>
    );
  }

  // ── Token valid; render wizard ────────────────────────────────────────────

  function validateSysadmin(): boolean {
    const e: Record<string, string> = {};
    if (!sysadmin.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sysadmin.email)) e.email = 'Invalid email';
    if (!sysadmin.password) e.password = 'Password is required';
    else if (sysadmin.password.length < 12) e.password = 'Password must be at least 12 characters';
    if (sysadmin.password !== sysadmin.passwordConfirm) {
      e.passwordConfirm = 'Passwords do not match';
    }
    setSysadminErrors(e);
    return Object.keys(e).length === 0;
  }

  function continueToTenant() {
    if (!validateSysadmin()) return;
    setPhase('tenant');
  }

  // The tenant wizard's onSuccess won't fire (it goes through useProvisionTenant
  // which hits the sysadmin endpoint). Instead, we render a special variant
  // here that captures the wizard's payload and submits via /setup/install.
  // Easier: render the wizard but intercept submit through a separate flow.
  //
  // Clean approach: render the wizard but in this page we submit through
  // useRedeemInstall. We'll build a simpler tenant form to avoid coupling.

  if (phase === 'sysadmin') {
    return (
      <CenteredCard>
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <CardTitle>Welcome to ArgusAI</CardTitle>
          </div>
          <CardDescription>
            Step 1 of 2 — Enroll the first platform administrator. This account is
            the root sysadmin: it provisions tenants, manages other sysadmins, and
            configures platform-wide settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FieldRow label="Email" error={sysadminErrors.email}>
            <Input
              type="email"
              autoComplete="username"
              value={sysadmin.email}
              onChange={(e) => setSysadmin((p) => ({ ...p, email: e.target.value }))}
              placeholder="root@argus.local"
            />
          </FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="First name (optional)">
              <Input
                value={sysadmin.firstName}
                onChange={(e) => setSysadmin((p) => ({ ...p, firstName: e.target.value }))}
              />
            </FieldRow>
            <FieldRow label="Last name (optional)">
              <Input
                value={sysadmin.lastName}
                onChange={(e) => setSysadmin((p) => ({ ...p, lastName: e.target.value }))}
              />
            </FieldRow>
          </div>
          <FieldRow label="Password" error={sysadminErrors.password} hint="Minimum 12 characters">
            <Input
              type="password"
              autoComplete="new-password"
              value={sysadmin.password}
              onChange={(e) => setSysadmin((p) => ({ ...p, password: e.target.value }))}
            />
          </FieldRow>
          <FieldRow label="Confirm password" error={sysadminErrors.passwordConfirm}>
            <Input
              type="password"
              autoComplete="new-password"
              value={sysadmin.passwordConfirm}
              onChange={(e) => setSysadmin((p) => ({ ...p, passwordConfirm: e.target.value }))}
            />
          </FieldRow>
          <div className="flex items-start gap-2 pt-2">
            <Checkbox
              id="totp"
              checked={sysadmin.totpEnrollment}
              onCheckedChange={(v) => setSysadmin((p) => ({ ...p, totpEnrollment: !!v }))}
            />
            <Label htmlFor="totp" className="cursor-pointer text-sm font-normal leading-tight">
              Enroll TOTP (recommended)
              <div className="text-xs text-muted-foreground mt-0.5">
                Required for sysadmin context selection on subsequent logins. We'll show the QR code
                after install.
              </div>
            </Label>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={continueToTenant}>Continue</Button>
          </div>
        </CardContent>
      </CenteredCard>
    );
  }

  if (phase === 'tenant') {
    return (
      <FirstTenantPhase
        token={token}
        sysadmin={sysadmin}
        onBack={() => setPhase('sysadmin')}
        onComplete={(r) => {
          setInstallResult(r);
          setPhase('done');
        }}
        redeem={redeem}
      />
    );
  }

  // ── done ─────────────────────────────────────────────────────────────────
  if (phase === 'done' && installResult) {
    return (
      <CenteredCard>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <CardTitle>Install complete</CardTitle>
          </div>
          <CardDescription>
            Sysadmin enrolled and first tenant provisioned. Capture any single-use secrets below
            before continuing — they cannot be retrieved later.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {installResult.sysadmin.totpSecret && (
            <div className="rounded border bg-muted/30 p-3 space-y-2">
              <div className="text-sm font-medium">TOTP enrollment</div>
              <div className="text-xs text-muted-foreground">
                Scan the QR URI with your authenticator app, or enter the secret manually.
              </div>
              <code className="block text-xs font-mono bg-background border rounded px-2 py-1.5 break-all">
                {installResult.sysadmin.qrUri}
              </code>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono bg-background border rounded px-2 py-1.5">
                  {installResult.sysadmin.totpSecret}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard.writeText(installResult.sysadmin.totpSecret!);
                    setCopiedSecret(true);
                    setTimeout(() => setCopiedSecret(false), 1500);
                  }}
                >
                  {copiedSecret ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
            </div>
          )}
          {installResult.invitation && (
            <div className="rounded border bg-muted/30 p-3 space-y-2">
              <div className="text-sm font-medium">First admin invitation</div>
              <div className="text-xs text-muted-foreground">
                Send this URL to the new tenant admin. Expires{' '}
                {new Date(installResult.invitation.expiresAt).toLocaleString()}.
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono bg-background border rounded px-2 py-1.5 break-all">
                  {installResult.invitation.inviteUrl}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard.writeText(installResult.invitation!.inviteUrl);
                    setCopiedToken(true);
                    setTimeout(() => setCopiedToken(false), 1500);
                  }}
                >
                  {copiedToken ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
            </div>
          )}
          <Separator />
          <div className="text-sm text-muted-foreground">
            Tenant <strong>{installResult.tenant.name}</strong> (
            <code className="font-mono text-xs">{installResult.tenant.slug}</code>) is ready.
            {installResult.signingPolicyReconciled && (
              <span className="text-amber-700 dark:text-amber-400 block mt-2 text-xs">
                Signing policy auto-upgraded to FIPS dual-sign for ITAR/FedRAMP profile.
              </span>
            )}
          </div>
          <div className="flex justify-end">
            <Button onClick={() => void navigate({ to: '/login' })}>Go to login</Button>
          </div>
        </CardContent>
      </CenteredCard>
    );
  }

  return null;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="max-w-md w-full">{children}</Card>
    </div>
  );
}

function FieldRow({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
      {hint && !error && <div className="text-xs text-muted-foreground">{hint}</div>}
      {error && <div className="text-xs text-destructive">{error}</div>}
    </div>
  );
}

// ─── First-tenant phase ──────────────────────────────────────────────────────
//
// We render the ProvisionTenantWizard inline-controlled. To avoid a second
// API hit through useProvisionTenant, we capture the wizard's payload via
// a custom embedded mode below. For v1 we render a simplified inline form
// covering the essentials and submit via useRedeemInstall.

interface FirstTenantPhaseProps {
  token: string;
  sysadmin: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    totpEnrollment: boolean;
  };
  onBack: () => void;
  onComplete: (r: RedeemInstallResponse) => void;
  redeem: ReturnType<typeof useRedeemInstall>;
}

function FirstTenantPhase({
  token,
  sysadmin,
  onBack,
  onComplete,
  redeem,
}: FirstTenantPhaseProps) {
  const [open, setOpen] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // The wizard's onSuccess fires after a successful provision, but the
  // wizard internally calls the sysadmin endpoint. For the install path
  // we want a different submission. The cleanest approach is to render a
  // dedicated install-tenant form here (no wizard reuse for this branch).
  //
  // For brevity v1 renders a lean form (name + slug + orgCode + compliance
  // profile + first admin) that covers the install bootstrap. Operators
  // can edit feature toggles + SSO via the admin dashboard once logged in.

  const [tName, setTName] = useState('');
  const [tSlug, setTSlug] = useState('');
  const [tOrgCode, setTOrgCode] = useState('');
  const [tProfile, setTProfile] = useState<RedeemInstallBody['firstTenant']['complianceProfile']>('none');
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!tName.trim()) e.name = 'Name is required';
    if (!/^[a-z][a-z0-9-]{2,49}$/.test(tSlug)) {
      e.slug = 'Slug must be lowercase letters, digits, hyphens (start with a letter, 3-50 chars)';
    }
    if (!/^[A-Z][A-Z0-9-]{2,29}$/.test(tOrgCode)) {
      e.orgCode = 'Org code must be UPPERCASE letters, digits, hyphens (start with letter, 3-30 chars)';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setSubmitError(null);
    try {
      const r = await redeem.mutateAsync({
        token,
        sysadmin: {
          email: sysadmin.email.trim(),
          password: sysadmin.password,
          firstName: sysadmin.firstName.trim() || undefined,
          lastName: sysadmin.lastName.trim() || undefined,
        },
        firstTenant: {
          name: tName.trim(),
          slug: tSlug.trim(),
          orgCode: tOrgCode.trim(),
          mode: 'standalone',
          complianceProfile: tProfile,
        },
        totpEnrollmentRequest: sysadmin.totpEnrollment,
      });
      onComplete(r);
    } catch (err) {
      const e = err as Error & { code?: string };
      setSubmitError(`${e.code ? `[${e.code}] ` : ''}${e.message}`);
    }
  }

  // We don't use the ProvisionTenantWizard for this phase to keep the install
  // flow lean (no SSO / features / resource limits — those live in the admin
  // dashboard once logged in). open/setOpen kept for future use.
  void open;
  void setOpen;

  return (
    <CenteredCard>
      <CardHeader>
        <CardTitle>Step 2 of 2 — First tenant</CardTitle>
        <CardDescription>
          Provision the first tenant. You can configure feature toggles, SSO, and resource limits
          later from the admin dashboard.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FieldRow label="Tenant name" error={errors.name}>
          <Input value={tName} onChange={(e) => setTName(e.target.value)} placeholder="Acme Industrial" />
        </FieldRow>
        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Slug" error={errors.slug} hint="lowercase, hyphens">
            <Input
              value={tSlug}
              onChange={(e) => setTSlug(e.target.value.toLowerCase())}
              placeholder="acme-industrial"
              className="font-mono"
            />
          </FieldRow>
          <FieldRow label="Org code" error={errors.orgCode} hint="UPPERCASE, hyphens">
            <Input
              value={tOrgCode}
              onChange={(e) => setTOrgCode(e.target.value.toUpperCase())}
              placeholder="ACME-IND"
              className="font-mono"
            />
          </FieldRow>
        </div>
        <FieldRow label="Compliance profile">
          <select
            value={tProfile}
            onChange={(e) => setTProfile(e.target.value as RedeemInstallBody['firstTenant']['complianceProfile'])}
            className="h-9 w-full rounded border bg-background px-3 text-sm"
          >
            <option value="none">None</option>
            <option value="soc2">SOC 2</option>
            <option value="iso27001">ISO 27001</option>
            <option value="hipaa">HIPAA</option>
            <option value="itar">ITAR (FIPS auto-upgrade)</option>
            <option value="fedramp">FedRAMP (FIPS auto-upgrade)</option>
          </select>
        </FieldRow>

        {submitError && (
          <div className="rounded border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {submitError}
          </div>
        )}

        <div className="flex justify-between gap-2 pt-2">
          <Button variant="outline" onClick={onBack} disabled={redeem.isPending}>
            Back
          </Button>
          <Button onClick={submit} disabled={redeem.isPending}>
            {redeem.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Installing…
              </>
            ) : (
              'Complete install'
            )}
          </Button>
        </div>
      </CardContent>
    </CenteredCard>
  );
}

// ProvisionTenantWizard import retained for future use when we extend the
// install flow to expose the full wizard (post-v1).
void ProvisionTenantWizard;
